import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGlobal } from '../context/GlobalContext';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { formatImageUrl } from '../utils/formatImageUrl';

const emptyAddressForm = {
    fullName: '',
    mobile: '',
    email: '',
    street: '',
    landmark: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    type: 'home',
    isDefault: false,
};

const loadRazorpayScript = () => {
    return new Promise((resolve) => {
        if (window.Razorpay) {
            resolve(true);
            return;
        }
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
};

const Checkout = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { cartItems, user, fetchCart } = useGlobal();

    const [buyNowItem, setBuyNowItem] = useState(null);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            toast.error("Please login to access checkout.", { id: 'checkout-auth-toast' });
            navigate('/login?redirect=/checkout', { replace: true, state: { from: '/checkout', buyNowItem: location.state?.buyNowItem } });
        }
    }, [navigate, location.state]);

    useEffect(() => {
        if (location.state?.buyNowItem) {
            setBuyNowItem(location.state.buyNowItem);
        }
    }, [location.state]);

    const checkoutItems = useMemo(() => {
        if (buyNowItem) {
            return [{
                ...buyNowItem,
                id: buyNowItem.id || buyNowItem.productId,
                productId: buyNowItem.productId || buyNowItem.id,
                name: buyNowItem.name || buyNowItem.title,
                image: buyNowItem.image || (buyNowItem.images && buyNowItem.images[0]) || '/assets/images/thumbs/product-img1.png',
                quantity: buyNowItem.quantity || 1,
                price: buyNowItem.price,
                currentPrice: buyNowItem.currentPrice || buyNowItem.price,
                variantName: buyNowItem.variantName || null,
                selectedVariantId: buyNowItem.selectedVariantId || null,
                product: buyNowItem.product || buyNowItem,
                variant: buyNowItem.variant || buyNowItem.selectedVariant || null,
                shippingWeight: buyNowItem.shippingWeight ?? buyNowItem.variant?.shippingWeight ?? buyNowItem.product?.shippingWeight ?? null,
                shippingDimensions: buyNowItem.shippingDimensions ?? buyNowItem.variant?.shippingDimensions ?? buyNowItem.product?.shippingDimensions ?? null,
            }];
        }
        return cartItems;
    }, [buyNowItem, cartItems]);

    const getPrice = (item) => {
        if (typeof item.price === 'number') return item.price;
        if (typeof item.currentPrice === 'number') return item.currentPrice;
        if (typeof item.currentPrice === 'string') return parseFloat(item.currentPrice.replace(/[^0-9.]/g, ''));
        if (typeof item.price === 'string') return parseFloat(item.price.replace(/[^0-9.]/g, ''));
        return 0;
    };

    const subtotal = useMemo(() => {
        return checkoutItems.reduce((acc, item) => {
            return acc + (getPrice(item) * (item.quantity || 1));
        }, 0);
    }, [checkoutItems]);

    // Shiprocket shipping state
    const [rawShippingCharge, setRawShippingCharge] = useState(0);
    const [courierName, setCourierName] = useState('Standard Shipping');
    const [selectedCourierId, setSelectedCourierId] = useState(null);
    const [estimatedDeliveryDays, setEstimatedDeliveryDays] = useState(4);
    const [isShippingLoading, setIsShippingLoading] = useState(false);
    const [availableCouriers, setAvailableCouriers] = useState([]);
    const [showCourierModal, setShowCourierModal] = useState(false);
    const [tempSelectedCourier, setTempSelectedCourier] = useState(null);

    // Coupon & Savings State
    const [availableCoupons, setAvailableCoupons] = useState([]);
    const [showCouponsDrawer, setShowCouponsDrawer] = useState(true);
    const [couponCodeInput, setCouponCodeInput] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
    const [isAutoApplyUserOverridden, setIsAutoApplyUserOverridden] = useState(false);

    // Saved addresses
    const [savedAddresses, setSavedAddresses] = useState([]);
    const [selectedAddressId, setSelectedAddressId] = useState(null);
    const [selectedBillingAddressId, setSelectedBillingAddressId] = useState(null);
    const [showAddressForm, setShowAddressForm] = useState(false);
    const [editingAddressId, setEditingAddressId] = useState(null);
    const [addressForm, setAddressForm] = useState(emptyAddressForm);

    const [isBillingSame, setIsBillingSame] = useState(true);
    const [paymentMethod, setPaymentMethod] = useState('upi');
    const [isPlacingOrder, setIsPlacingOrder] = useState(false);

    // Fetch user addresses from the backend
    const fetchAddresses = useCallback(async () => {
        try {
            const res = await api.get('/api/address');
            setSavedAddresses(res.data);
            if (res.data.length > 0) {
                const def = res.data.find(a => a.isDefault);
                if (def && !selectedAddressId) {
                    setSelectedAddressId(def.id);
                    setSelectedBillingAddressId(def.id);
                } else if (!selectedAddressId) {
                    setSelectedAddressId(res.data[0].id);
                    setSelectedBillingAddressId(res.data[0].id);
                }
            }
        } catch (error) {
            console.error("Failed to fetch addresses:", error);
        }
    }, [selectedAddressId]);

    useEffect(() => {
        fetchAddresses();
    }, [fetchAddresses]);

    // Load cached coupon from localStorage on mount
    useEffect(() => {
        try {
            const cached = localStorage.getItem('bb_applied_coupon');
            if (cached) {
                const parsed = JSON.parse(cached);
                if (parsed && parsed.code) {
                    setAppliedCoupon(parsed);
                    setIsAutoApplyUserOverridden(true);
                }
            }
        } catch (e) {
            console.error('Error reading cached coupon:', e);
        }
    }, []);

    const fetchAvailableCoupons = useCallback(async () => {
        try {
            const params = { showInCheckoutOnly: 'true' };
            if (user?.id) params.customerId = user.id;
            if (subtotal > 0) params.subtotal = subtotal;

            let res;
            try {
                res = await api.get('/api/coupons/available', { params });
            } catch (_) {
                res = await api.get('/api/coupons', { params });
            }

            const list = Array.isArray(res.data)
                ? res.data
                : (Array.isArray(res.data?.coupons) ? res.data.coupons : []);
            setAvailableCoupons(list);
        } catch (err) {
            console.error('Failed to fetch available coupons:', err);
        }
    }, [user?.id, subtotal]);

    useEffect(() => {
        fetchAvailableCoupons();
    }, [fetchAvailableCoupons]);

    const selectedAddress = savedAddresses.find(a => a.id === selectedAddressId);
    const lastRateCalcSignatureRef = useRef('');

    // Shiprocket live rate calculation
    const fetchShiprocketRates = useCallback(async () => {
        if (!selectedAddress || !selectedAddress.pincode) return;

        const parseDimensionObj = (val) => {
            if (!val) return null;
            let d = val;
            if (typeof d === 'string') {
                try { d = JSON.parse(d); } catch { return null; }
            }
            if (typeof d !== 'object' || d === null) return null;
            const l = parseFloat(d.length);
            const b = parseFloat(d.breadth || d.width);
            const h = parseFloat(d.height);
            if (!isNaN(l) && l > 0 && !isNaN(b) && b > 0 && !isNaN(h) && h > 0) {
                return { length: l, breadth: b, height: h };
            }
            return null;
        };

        const extractWeightFromText = (text) => {
            if (!text || typeof text !== 'string') return null;
            const kgMatch = text.match(/(\d+(?:\.\d+)?)\s*(?:kg|kgs|kilo|kilogram)/i);
            if (kgMatch) return parseFloat(kgMatch[1]);
            const gmMatch = text.match(/(\d+(?:\.\d+)?)\s*(?:g|gm|gms|gram|grams)/i);
            if (gmMatch) return parseFloat(gmMatch[1]) / 1000;
            const ltrMatch = text.match(/(\d+(?:\.\d+)?)\s*(?:l|ltr|litre|litres|liter|liters)/i);
            if (ltrMatch) return parseFloat(ltrMatch[1]);
            const mlMatch = text.match(/(\d+(?:\.\d+)?)\s*(?:ml|millilitre|milliliter)/i);
            if (mlMatch) return parseFloat(mlMatch[1]) / 1000;
            return null;
        };

        const getItemWeightAndDims = (item) => {
            const prod = item.product || {};
            let allVariants = [];
            if (Array.isArray(prod.Variants)) allVariants.push(...prod.Variants);
            if (Array.isArray(prod.variants)) allVariants.push(...prod.variants);
            if (Array.isArray(prod.rawVariants)) allVariants.push(...prod.rawVariants);
            if (typeof prod.variation === 'string') {
                try {
                    const parsedVar = JSON.parse(prod.variation);
                    if (Array.isArray(parsedVar)) allVariants.push(...parsedVar);
                } catch { }
            } else if (Array.isArray(prod.variation)) {
                allVariants.push(...prod.variation);
            }

            const variant = allVariants.find(v => String(v.id) === String(item.selectedVariantId))
                || item.variant
                || item.selectedVariant
                || {};

            // 1. Resolve weight (in kg)
            const rawWeight = variant.shippingWeight
                ?? prod.shippingWeight
                ?? item.shippingWeight
                ?? item.weight
                ?? extractWeightFromText(item.variantName || variant.variantName)
                ?? extractWeightFromText(item.name || prod.name);

            let itemWeight = (rawWeight !== undefined && rawWeight !== null && rawWeight !== '' && !isNaN(parseFloat(rawWeight)))
                ? parseFloat(rawWeight)
                : 0.5;
            if (isNaN(itemWeight) || itemWeight <= 0) itemWeight = 0.5;

            // 2. Resolve dimensions (in cm)
            const validDims = parseDimensionObj(variant.shippingDimensions)
                || parseDimensionObj(prod.shippingDimensions)
                || parseDimensionObj(item.shippingDimensions)
                || parseDimensionObj(item.dimensions);

            const length = validDims?.length || 10;
            const breadth = validDims?.breadth || 10;
            const height = validDims?.height || 10;

            return { weight: itemWeight, length, breadth, height };
        };

        let totalDeadWeight = 0;
        let maxLen = 0, maxBrd = 0, totalHgt = 0;

        checkoutItems.forEach(i => {
            const { weight, length, breadth, height } = getItemWeightAndDims(i);
            const qty = parseInt(i.quantity || 1, 10);
            totalDeadWeight += weight * qty;
            if (length > maxLen) maxLen = length;
            if (breadth > maxBrd) maxBrd = breadth;
            totalHgt += height * qty;
        });

        const sendWeight = Math.max(parseFloat(totalDeadWeight.toFixed(3)), 0.05);
        const sendLength = Math.max(parseFloat((maxLen || 10).toFixed(1)), 0.5);
        const sendBreadth = Math.max(parseFloat((maxBrd || 10).toFixed(1)), 0.5);
        const sendHeight = Math.max(parseFloat((totalHgt || 10).toFixed(1)), 0.5);

        const currentSignature = `${selectedAddress.pincode}_${sendWeight}_${sendLength}_${sendBreadth}_${sendHeight}_${subtotal}`;
        if (lastRateCalcSignatureRef.current === currentSignature) {
            return;
        }

        setIsShippingLoading(true);
        try {
            const res = await api.post('/api/shipping/calculate-rate', {
                delivery_postcode: selectedAddress.pincode,
                weight: sendWeight,
                length: sendLength,
                breadth: sendBreadth,
                height: sendHeight,
                cod: 0,
                order_subtotal: subtotal
            });

            lastRateCalcSignatureRef.current = currentSignature;

            if (res.data && res.data.success) {
                const couriersList = res.data.allCouriers || [];
                // Sort couriers by rate ascending (lowest price first)
                const sortedCouriers = [...couriersList].sort((a, b) => {
                    const rateA = parseFloat(a.charge !== undefined ? a.charge : a.rate || 0);
                    const rateB = parseFloat(b.charge !== undefined ? b.charge : b.rate || 0);
                    return rateA - rateB;
                });
                setAvailableCouriers(sortedCouriers);

                setSelectedCourierId(prevId => {
                    const currentMatch = sortedCouriers.find(c => String(c.id) === String(prevId));
                    const chosenCourier = currentMatch || sortedCouriers[0] || {
                        charge: res.data.rate || 0,
                        name: res.data.courier_name || 'Shiprocket Delivery',
                        days: res.data.estimated_delivery_days || 4,
                        id: 1,
                    };

                    const rate = parseFloat(chosenCourier.charge !== undefined ? chosenCourier.charge : (chosenCourier.rate || res.data.rate || 0));
                    setRawShippingCharge(rate);
                    setCourierName(chosenCourier.name || res.data.courier_name || 'Standard Delivery');
                    setEstimatedDeliveryDays(chosenCourier.days || res.data.estimated_delivery_days || 4);
                    return chosenCourier.id || null;
                });
            }
        } catch (err) {
            console.error('Shiprocket rate check failed:', err);
        } finally {
            setIsShippingLoading(false);
        }
    }, [selectedAddress, checkoutItems, subtotal]);

    useEffect(() => {
        if (selectedAddressId) {
            fetchShiprocketRates();
        }
    }, [selectedAddressId, fetchShiprocketRates]);

    // Handle Opening Delivery Partner Modal
    const handleOpenCourierModal = () => {
        if (!selectedAddress || !selectedAddress.pincode) {
            toast.error('Please select or add a delivery address first.');
            return;
        }
        if (availableCouriers.length === 0) {
            toast.error('No courier partners found for this address. Please verify your pincode.');
            return;
        }
        const active = availableCouriers.find(c => String(c.id) === String(selectedCourierId)) || availableCouriers[0];
        setTempSelectedCourier(active);
        setShowCourierModal(true);
    };

    // Handle Confirming Courier Selection from Modal
    const handleConfirmCourier = () => {
        if (!tempSelectedCourier) return;
        const rate = parseFloat(tempSelectedCourier.charge !== undefined ? tempSelectedCourier.charge : (tempSelectedCourier.rate || 0));
        setRawShippingCharge(rate);
        setCourierName(tempSelectedCourier.name);
        setSelectedCourierId(tempSelectedCourier.id);
        setEstimatedDeliveryDays(tempSelectedCourier.days || 4);

        setShowCourierModal(false);
        toast.success(`Delivery Partner selected: ${tempSelectedCourier.name} (₹${rate.toFixed(2)})`);
    };

    // Calculate discount for any coupon against current subtotal
    const calculateCouponDiscount = useCallback((coupon, cartSubtotal) => {
        if (!coupon || coupon.isLimitReached) return 0;
        const minOrder = parseFloat(coupon.minOrderValue ?? coupon.min_order ?? 0);
        if (cartSubtotal < minOrder) return 0;

        const cType = (coupon.type || 'PERCENT').toUpperCase();
        const cVal = parseFloat(coupon.value || 0);

        if (cType === 'PERCENT') {
            let disc = (cartSubtotal * cVal) / 100;
            const maxDisc = parseFloat(coupon.maxDiscount ?? coupon.max_discount);
            if (!isNaN(maxDisc) && maxDisc > 0) {
                disc = Math.min(disc, maxDisc);
            }
            return parseFloat(disc.toFixed(2));
        }
        if (cType === 'FLAT') {
            return parseFloat(Math.min(cVal, cartSubtotal).toFixed(2));
        }
        if (cType === 'FREE_SHIPPING') {
            return parseFloat((rawShippingCharge || 0).toFixed(2));
        }
        return 0;
    }, [rawShippingCharge]);

    // Compute Best / Max Savings Coupon (ignoring limit-reached coupons)
    const bestCoupon = useMemo(() => {
        if (!availableCoupons || availableCoupons.length === 0 || subtotal <= 0) return null;
        let maxSavings = 0;
        let best = null;

        for (const c of availableCoupons) {
            if (c.isLimitReached) continue;
            const savings = calculateCouponDiscount(c, subtotal);
            if (savings > maxSavings) {
                maxSavings = savings;
                best = { ...c, calculatedDiscount: savings };
            }
        }
        return best;
    }, [availableCoupons, subtotal, calculateCouponDiscount]);

    // Check and silently clear applied coupon if its limit is reached
    useEffect(() => {
        if (appliedCoupon && availableCoupons.length > 0) {
            const matched = availableCoupons.find(c => c.code === appliedCoupon.code || c.id === appliedCoupon.id);
            if (matched && matched.isLimitReached) {
                setAppliedCoupon(null);
                localStorage.removeItem('bb_applied_coupon');
            }
        }
    }, [availableCoupons, appliedCoupon]);

    // Smart Auto-Apply Engine
    useEffect(() => {
        if (subtotal <= 0 || isAutoApplyUserOverridden) return;

        if (!appliedCoupon && bestCoupon && bestCoupon.calculatedDiscount > 0) {
            setAppliedCoupon({
                id: bestCoupon.id,
                code: bestCoupon.code,
                type: bestCoupon.type,
                value: bestCoupon.value,
                discount: bestCoupon.calculatedDiscount,
                isAutoApplied: true,
            });
            try {
                localStorage.setItem('bb_applied_coupon', JSON.stringify({
                    id: bestCoupon.id,
                    code: bestCoupon.code,
                    type: bestCoupon.type,
                    value: bestCoupon.value,
                    discount: bestCoupon.calculatedDiscount,
                }));
            } catch (_) {}
        } else if (appliedCoupon) {
            const currentDiscount = calculateCouponDiscount(appliedCoupon, subtotal);
            if (currentDiscount !== appliedCoupon.discount) {
                if (currentDiscount > 0) {
                    setAppliedCoupon(prev => ({ ...prev, discount: currentDiscount }));
                } else {
                    setAppliedCoupon(null);
                    localStorage.removeItem('bb_applied_coupon');
                }
            }
        }
    }, [subtotal, bestCoupon, appliedCoupon, isAutoApplyUserOverridden, calculateCouponDiscount]);

    // Handle Manual Coupon Application
    const handleApplyCoupon = async (e) => {
        if (e) e.preventDefault();
        const codeToApply = couponCodeInput.trim().toUpperCase();
        if (!codeToApply) {
            toast.error('Please enter a coupon code');
            return;
        }

        setIsApplyingCoupon(true);
        try {
            const res = await api.post('/api/coupons/validate', {
                code: codeToApply,
                subtotal: subtotal,
                customerId: user?.id || null,
            });

            if (res.data && res.data.valid) {
                const discount = parseFloat(res.data.discountAmount) || 0;
                const cObj = {
                    id: res.data.coupon?.id,
                    code: res.data.coupon_code || codeToApply,
                    type: res.data.type || 'PERCENT',
                    value: res.data.value,
                    discount: discount,
                    freeShipping: res.data.freeShipping || false,
                };
                setAppliedCoupon(cObj);
                setIsAutoApplyUserOverridden(true);
                localStorage.setItem('bb_applied_coupon', JSON.stringify(cObj));
                toast.success(`Coupon "${codeToApply}" applied! Saved ₹${discount.toFixed(2)}`);
                setCouponCodeInput('');
                setShowCouponsDrawer(false);
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Invalid or expired coupon code');
        } finally {
            setIsApplyingCoupon(false);
        }
    };

    // Apply specific coupon from drawer
    const handleSelectCoupon = (coupon) => {
        if (coupon.isLimitReached) return;
        const discount = calculateCouponDiscount(coupon, subtotal);
        if (discount <= 0 && (coupon.type || '').toUpperCase() !== 'FREE_SHIPPING') {
            const minSpend = parseFloat(coupon.minOrderValue ?? coupon.min_order ?? 0);
            toast.error(`Add ₹${(minSpend - subtotal).toFixed(0)} more to activate "${coupon.code}"`);
            return;
        }

        const cObj = {
            id: coupon.id,
            code: coupon.code,
            type: coupon.type,
            value: coupon.value,
            discount: discount,
            freeShipping: (coupon.type || '').toUpperCase() === 'FREE_SHIPPING',
        };
        setAppliedCoupon(cObj);
        setIsAutoApplyUserOverridden(true);
        localStorage.setItem('bb_applied_coupon', JSON.stringify(cObj));
        toast.success(`Coupon "${coupon.code}" applied!`);
        setShowCouponsDrawer(false);
    };

    const handleRemoveCoupon = () => {
        setAppliedCoupon(null);
        setIsAutoApplyUserOverridden(true);
        localStorage.removeItem('bb_applied_coupon');
        toast.success('Coupon removed');
    };

    const handleResetToAutoSelect = () => {
        setIsAutoApplyUserOverridden(false);
        if (bestCoupon && bestCoupon.calculatedDiscount > 0) {
            setAppliedCoupon({
                id: bestCoupon.id,
                code: bestCoupon.code,
                type: bestCoupon.type,
                value: bestCoupon.value,
                discount: bestCoupon.calculatedDiscount,
                isAutoApplied: true,
            });
            localStorage.setItem('bb_applied_coupon', JSON.stringify({
                id: bestCoupon.id,
                code: bestCoupon.code,
                type: bestCoupon.type,
                value: bestCoupon.value,
                discount: bestCoupon.calculatedDiscount,
            }));
            toast.success(`Auto-applied best coupon: "${bestCoupon.code}"!`);
        }
    };

    const couponDiscount = appliedCoupon?.discount || 0;
    const shippingCharge = appliedCoupon?.freeShipping || (appliedCoupon?.type || '').toUpperCase() === 'FREE_SHIPPING'
        ? 0
        : rawShippingCharge;
    const finalTotal = Math.max(0, subtotal - couponDiscount + shippingCharge);

    // Address Modal helpers
    const openAddForm = () => {
        setEditingAddressId(null);
        setAddressForm(emptyAddressForm);
        setShowAddressForm(true);
    };

    const openEditForm = (addr) => {
        setEditingAddressId(addr.id);
        setAddressForm({
            fullName: addr.fullName || '',
            mobile: addr.phone || addr.mobile || '',
            email: addr.email || '',
            street: addr.street || '',
            landmark: addr.apartment || addr.landmark || '',
            city: addr.city || '',
            state: addr.state || '',
            pincode: addr.pincode || '',
            country: addr.country || 'India',
            type: (addr.addressType || addr.type || 'home').toLowerCase(),
            isDefault: addr.isDefault || false,
        });
        setShowAddressForm(true);
    };

    const closeForm = () => {
        setShowAddressForm(false);
        setEditingAddressId(null);
    };

    const deleteAddress = async (id) => {
        try {
            await api.delete(`/api/address/${id}`);
            toast.success("Address deleted!");
            fetchAddresses();
            if (selectedAddressId === id) setSelectedAddressId(null);
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to delete address");
        }
    };

    const saveAddress = async (e) => {
        e.preventDefault();
        const payload = {
            addressType: addressForm.type || 'Home',
            fullName: addressForm.fullName,
            phone: addressForm.mobile,
            email: addressForm.email || user?.email,
            street: addressForm.street,
            apartment: addressForm.landmark,
            city: addressForm.city,
            state: addressForm.state,
            pincode: addressForm.pincode,
            country: addressForm.country,
            isDefault: addressForm.isDefault,
        };

        try {
            if (editingAddressId) {
                await api.put(`/api/address/${editingAddressId}`, payload);
                toast.success("Address updated!");
            } else {
                const res = await api.post('/api/address', payload);
                toast.success("Address saved!");
                if (res.data?.id) {
                    setSelectedAddressId(res.data.id);
                }
            }
            fetchAddresses();
            closeForm();
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to save address");
        }
    };

    // Place Order Flow
    const handlePlaceOrder = async () => {
        if (!selectedAddressId) {
            toast.error("Please select a delivery address.");
            return;
        }
        if (!checkoutItems || checkoutItems.length === 0) {
            toast.error("Your cart is empty.");
            return;
        }

        setIsPlacingOrder(true);
        const selectedAddress = savedAddresses.find(a => a.id === selectedAddressId) || {};

        const items = checkoutItems.map(item => ({
            productId: item.productId || item.id,
            quantity: item.quantity || 1,
            price: getPrice(item),
            selectedVariantId: item.selectedVariantId || null,
            name: item.name || item.title,
            image: item.image
        }));

        const orderPayload = {
            items,
            totalAmount: finalTotal,
            shippingAddressId: selectedAddressId,
            billingAddressId: isBillingSame ? selectedAddressId : (selectedBillingAddressId || selectedAddressId),
            paymentMethod: 'upi',
            shippingCharge,
            courier: courierName,
            estimatedDeliveryDays,
            couponCode: appliedCoupon?.code || null,
            couponId: appliedCoupon?.id || null,
            couponDiscount: couponDiscount,
        };

        try {
            const orderRes = await api.post('/api/orders', orderPayload);
            const createdOrder = orderRes.data;

            localStorage.removeItem('bb_applied_coupon');

            const rzpOrderRes = await api.post('/api/payment/create-order', {
                amount: finalTotal,
                currency: 'INR',
                dbOrderId: createdOrder.id
            });

            const rzpData = rzpOrderRes.data;
            const razorpayOrderId = rzpData.orderId || rzpData.id;

            const isSdkLoaded = await loadRazorpayScript();
            if (!isSdkLoaded || !window.Razorpay) {
                toast.error("Failed to initialize Razorpay SDK. Please refresh and try again.");
                setIsPlacingOrder(false);
                return;
            }

            const razorpayKey = rzpData.key || process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_TBPx9EL1T1aqhC';

            let isPaymentCompleted = false;

            const options = {
                key: razorpayKey,
                amount: rzpData.amount,
                currency: rzpData.currency || 'INR',
                name: "Healthy Homes",
                description: `Order #${createdOrder.orderNumber || createdOrder.id}`,
                order_id: razorpayOrderId,
                modal: {
                    ondismiss: async function () {
                        if (!isPaymentCompleted) {
                            try {
                                await api.delete(`/api/payment/abort-pending-order/${createdOrder.id}`);
                            } catch (abortErr) {
                                console.warn("[Checkout] Could not abort unpaid pending order:", abortErr);
                            }
                        }
                    }
                },
                handler: async function (response) {
                    isPaymentCompleted = true;
                    try {
                        const verifyRes = await api.post('/api/payment/verify-order', {
                            razorpay_order_id: response.razorpay_order_id || razorpayOrderId,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature,
                            dbOrderId: createdOrder.id
                        });

                        if (verifyRes.data && (verifyRes.data.success || verifyRes.status === 200)) {
                            toast.success("Payment successful! Order placed.");
                            if (fetchCart) fetchCart();
                            setTimeout(() => {
                                navigate(`/order-confirmation/${createdOrder.id}`, { state: { order: createdOrder, orderId: createdOrder.id } });
                            }, 300);
                        } else {
                            toast.error("Payment verification failed. Please check with customer support.");
                        }
                    } catch (err) {
                        toast.error(err.response?.data?.message || "Payment verification failed.");
                    }
                },
                prefill: {
                    name: selectedAddress.fullName || user?.name || '',
                    email: selectedAddress.email || user?.email || '',
                    contact: selectedAddress.phone || selectedAddress.mobile || user?.phone || ''
                },
                theme: { color: "#10B981" },
            };

            const paymentObject = new window.Razorpay(options);
            paymentObject.on('payment.failed', function (response) {
                toast.error(`Payment failed: ${response.error?.description || 'Payment was unsuccessful'}`);
            });
            paymentObject.open();
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to place order.");
        } finally {
            setIsPlacingOrder(false);
        }
    };

    return (
        <section className="checkout py-40">
            <div className="container container-lg">
                <div className="row gy-4">
                    {/* Left Column: Address & Payment Selection */}
                    <div className="col-xl-7 col-lg-7">
                        {/* Delivery Address Card */}
                        <div className="co-card mb-24">
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-12 mb-20">
                                <div className="d-flex align-items-center gap-12">
                                    <span className="p-2 rounded-8 text-white flex-center flex-shrink-0" style={{ width: 34, height: 34, background: '#10B981' }}>
                                        <i className="ph-fill ph-map-pin text-base" />
                                    </span>
                                    <div>
                                        <h6 className="text-base text-sm-lg mb-0 font-heading fw-bold">1. Delivery Address</h6>
                                        <span className="text-xs text-gray-500 d-block">Select where you want your order delivered</span>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={openAddForm}
                                    className="btn btn-sm btn-outline-main rounded-8 px-12 py-6 text-xs fw-semibold flex-align gap-6 ms-auto"
                                >
                                    <i className="ph ph-plus" /> Add New Address
                                </button>
                            </div>

                            {/* Saved Addresses Grid */}
                            {savedAddresses.length > 0 ? (
                                <div className="row g-2 g-sm-3">
                                    {savedAddresses.map((addr) => {
                                        const isSelected = selectedAddressId === addr.id;
                                        return (
                                            <div key={addr.id} className="col-12 col-md-6">
                                                <div
                                                    onClick={() => setSelectedAddressId(addr.id)}
                                                    className={`p-12 p-sm-16 rounded-12 border cursor-pointer transition position-relative h-100 ${
                                                        isSelected ? 'border-main-600 shadow-sm' : 'border-gray-200 hover-border-gray-300'
                                                    }`}
                                                    style={{
                                                        background: isSelected ? '#F0FDF4' : '#FFFFFF',
                                                        borderColor: isSelected ? '#10B981' : '#E5E7EB',
                                                        borderWidth: isSelected ? '2px' : '1px',
                                                    }}
                                                >
                                                    <div className="d-flex align-items-center justify-content-between mb-8">
                                                        <span className="badge bg-gray-100 text-gray-700 text-xs px-8 py-2 rounded-4 text-capitalize">
                                                            {addr.addressType || addr.type || 'Home'}
                                                        </span>
                                                        <div className="flex-align gap-8">
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openEditForm(addr);
                                                                }}
                                                                className="text-gray-500 hover-text-main-600 text-sm p-4"
                                                                title="Edit"
                                                                aria-label="Edit address"
                                                            >
                                                                <i className="ph ph-pencil-simple" />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    deleteAddress(addr.id);
                                                                }}
                                                                className="text-gray-400 hover-text-danger-600 text-sm p-4"
                                                                title="Delete"
                                                                aria-label="Delete address"
                                                            >
                                                                <i className="ph ph-trash" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <h6 className="text-sm fw-bold text-neutral-900 mb-4">{addr.fullName}</h6>
                                                    <p className="text-xs text-gray-600 mb-4 text-break" style={{ lineHeight: '1.5' }}>
                                                        {addr.street}{addr.apartment ? `, ${addr.apartment}` : ''}, {addr.city}, {addr.state} - <strong>{addr.pincode}</strong>
                                                    </p>
                                                    <span className="text-xs text-gray-500 d-block">📞 {addr.phone || addr.mobile}</span>

                                                    {isSelected && (
                                                        <div className="position-absolute bottom-0 end-0 p-8">
                                                            <span className="badge rounded-circle flex-center" style={{ width: 20, height: 20, background: '#10B981', color: '#fff' }}>
                                                                <i className="ph-fill ph-check text-[10px]" />
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-24 bg-gray-50 rounded-12 border border-dashed border-gray-300">
                                    <i className="ph ph-map-pin text-3xl text-gray-400 mb-8 d-block" />
                                    <p className="text-sm text-gray-600 mb-12">No delivery addresses saved yet.</p>
                                    <button
                                        type="button"
                                        onClick={openAddForm}
                                        className="btn btn-main btn-sm rounded-8 px-16 py-8 text-xs"
                                    >
                                        Add Your First Address
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Payment Method Card */}
                        <div className="co-card">
                            <div className="d-flex align-items-center gap-12 mb-20">
                                <span className="p-2 rounded-8 text-white flex-center flex-shrink-0" style={{ width: 34, height: 34, background: '#3B82F6' }}>
                                    <i className="ph-fill ph-credit-card text-base" />
                                </span>
                                <div>
                                    <h6 className="text-base text-sm-lg mb-0 font-heading fw-bold">2. Payment Method</h6>
                                    <span className="text-xs text-gray-500 d-block">Choose your preferred payment method</span>
                                </div>
                            </div>

                            {/* Online / UPI option */}
                            <label
                                className={`d-flex align-items-start gap-12 p-12 p-sm-16 rounded-12 border cursor-pointer mb-12 transition ${
                                    paymentMethod === 'upi' ? 'border-main-600 bg-main-50/20' : 'border-gray-200 hover-border-gray-300'
                                }`}
                                style={{
                                    background: paymentMethod === 'upi' ? '#F0FDF4' : '#FFFFFF',
                                    borderColor: paymentMethod === 'upi' ? '#10B981' : '#E5E7EB',
                                    borderWidth: paymentMethod === 'upi' ? '2px' : '1px',
                                }}
                            >
                                <input
                                    type="radio"
                                    name="payment_method"
                                    value="upi"
                                    checked={paymentMethod === 'upi'}
                                    onChange={() => setPaymentMethod('upi')}
                                    className="form-check-input mt-1 flex-shrink-0"
                                />
                                <div className="flex-grow-1 min-w-0">
                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-6 mb-2">
                                        <span className="text-xs text-sm-sm fw-bold text-neutral-900">Online Payment (UPI, Cards, NetBanking, Wallets)</span>
                                        {/* <span className="badge text-[10px] px-8 py-2 rounded-pill flex-shrink-0" style={{ background: '#D1FAE5', color: '#065F46' }}>
                                            100% Secure via Razorpay
                                        </span> */}
                                    </div>
                                    <span className="d-block text-gray-500 text-xs" style={{ lineHeight: '1.4' }}>
                                        Instant confirmation • Google Pay, PhonePe, Paytm, Cards & UPI
                                    </span>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Right Column: Order Summary & Coupon Engine */}
                    <div className="col-xl-5 col-lg-5">
                        <div className="co-summary-card position-sticky top-0">
                            <div className="flex-between mb-20">
                                <h6 className="text-lg mb-0 font-heading fw-bold">Order Summary</h6>
                                <span className="bg-color-three text-gray-700 text-xs fw-semibold py-4 px-12 rounded-pill">
                                    {checkoutItems.length} {checkoutItems.length === 1 ? 'item' : 'items'}
                                </span>
                            </div>

                            {/* Cart Items List */}
                            <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '16px' }}>
                                {checkoutItems.map((item) => (
                                    <div className="co-item-row" key={item.cartItemId || item.id}>
                                        <img
                                            src={formatImageUrl(item.image) || '/assets/images/thumbs/product-img1.png'}
                                            alt={item.name}
                                            className="co-item-thumb"
                                            onError={(e) => {
                                                e.currentTarget.onerror = null;
                                                e.currentTarget.src = '/assets/images/thumbs/product-img1.png';
                                            }}
                                        />
                                        <div className="flex-grow-1">
                                            <p className="mb-0 text-xs fw-semibold text-neutral-900 text-line-2">{item.name}</p>
                                            {item.variantName && <span className="text-[11px] text-gray-500 d-block">{item.variantName}</span>}
                                            <span className="text-[11px] text-gray-500 d-block mt-2">Qty: {item.quantity || 1}</span>
                                        </div>
                                        <span className="fw-semibold text-xs text-neutral-900 flex-shrink-0">
                                            ₹{(getPrice(item) * item.quantity).toFixed(2)}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            {/* ── 🎟️ Coupon & Discounts Section ── */}
                            <div className="mt-16 mb-16">
                                {appliedCoupon ? (
                                    <div className="co-coupon-applied-box mb-12">
                                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-8">
                                            <div className="d-flex align-items-center gap-10 flex-grow-1 min-w-0">
                                                <span className="p-2 rounded-8 text-white flex-center flex-shrink-0" style={{ width: 32, height: 32, background: '#10B981' }}>
                                                    <i className="ph-fill ph-ticket text-base" />
                                                </span>
                                                <div className="min-w-0">
                                                    <div className="d-flex align-items-center gap-6 flex-wrap">
                                                        <span className="co-coupon-code-tag">
                                                            {appliedCoupon.code}
                                                        </span>
                                                        {appliedCoupon.code === bestCoupon?.code && (
                                                            <span className="badge px-8 py-2 rounded-pill fw-bold" style={{ fontSize: '10px', background: '#D1FAE5', color: '#065F46', lineHeight: 1.2 }}>
                                                                ★ Max Savings
                                                            </span>
                                                        )}
                                                    </div>
                                                    <span className="text-xs fw-semibold d-block mt-2" style={{ color: '#047857' }}>
                                                        {appliedCoupon.freeShipping || (appliedCoupon.type || '').toUpperCase() === 'FREE_SHIPPING'
                                                            ? '✓ 100% Free Shipping Applied'
                                                            : `✓ Saved ₹${Math.round(couponDiscount)} on this order`}
                                                    </span>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={handleRemoveCoupon}
                                                className="btn btn-sm px-10 py-4 text-xs fw-bold rounded-8 text-danger-600 hover-bg-danger-50 transition border border-danger-200 ms-auto"
                                                title="Remove coupon"
                                            >
                                                ✕ Remove
                                            </button>
                                        </div>

                                        {/* Notice if a higher discount coupon exists */}
                                        {bestCoupon && bestCoupon.code !== appliedCoupon.code && bestCoupon.calculatedDiscount > couponDiscount && (
                                            <div className="mt-8 pt-8 border-top border-emerald-200 d-flex align-items-center justify-content-between flex-wrap gap-6 text-xs text-emerald-800">
                                                <span>
                                                    💡 Better offer available: <strong>{bestCoupon.code}</strong> saves ₹{Math.round(bestCoupon.calculatedDiscount)}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={handleResetToAutoSelect}
                                                    className="btn p-0 text-emerald-700 hover-text-emerald-900 fw-bold underline text-xs"
                                                >
                                                    Switch to Best
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <form onSubmit={handleApplyCoupon} className="d-flex gap-8 mb-12">
                                        <div className="position-relative flex-grow-1">
                                            <i className="ph ph-ticket position-absolute start-0 top-50 translate-middle-y ms-12 text-gray-400 text-sm" />
                                            <input
                                                type="text"
                                                className="form-control text-xs uppercase font-mono fw-bold py-8 ps-36 pe-12 rounded-8"
                                                placeholder="Enter coupon code (e.g. SAVE20)"
                                                value={couponCodeInput}
                                                onChange={(e) => setCouponCodeInput(e.target.value.toUpperCase().replace(/\s+/g, ''))}
                                                style={{ borderColor: '#E5E7EB' }}
                                            />
                                        </div>
                                        <button
                                            type="submit"
                                            className="btn btn-main px-18 py-8 rounded-8 text-xs fw-bold flex-shrink-0"
                                            disabled={isApplyingCoupon || !couponCodeInput.trim()}
                                        >
                                            {isApplyingCoupon ? (
                                                <span className="flex-align gap-4">
                                                    <i className="ph ph-spinner-gap ph-spin" /> Applying...
                                                </span>
                                            ) : (
                                                'Apply'
                                            )}
                                        </button>
                                    </form>
                                )}

                                {/* Available Coupons Accordion */}
                                {availableCoupons.length > 0 && (
                                    <div className="border border-gray-200 rounded-12 overflow-hidden bg-white mb-16 shadow-xs">
                                        <button
                                            type="button"
                                            onClick={() => setShowCouponsDrawer(!showCouponsDrawer)}
                                            className="w-100 p-12 p-sm-14 flex-between text-start text-xs fw-bold text-neutral-800 hover-bg-gray-50 transition"
                                            style={{ background: '#FFFFFF', border: 'none' }}
                                        >
                                            <span className="flex-align gap-8 flex-wrap">
                                                <span className="p-1 rounded-6 text-white flex-center" style={{ width: 22, height: 22, background: '#10B981' }}>
                                                    <i className="ph-fill ph-ticket text-xs" />
                                                </span>
                                                <span>Available Coupons ({availableCoupons.length})</span>
                                                {bestCoupon && bestCoupon.calculatedDiscount > 0 && (
                                                    <span className="badge px-8 py-2 rounded-pill fw-bold" style={{ fontSize: '10.5px', background: '#D1FAE5', color: '#065F46', lineHeight: 1.2 }}>
                                                        Save up to ₹{bestCoupon.calculatedDiscount.toFixed(0)}
                                                    </span>
                                                )}
                                            </span>
                                            <i className={`ph ${showCouponsDrawer ? 'ph-caret-up' : 'ph-caret-down'} text-gray-500 text-sm`} />
                                        </button>

                                        {showCouponsDrawer && (
                                            <div className="p-12 border-top border-gray-100 space-y-8" style={{ maxHeight: 260, overflowY: 'auto', background: '#F8FAFC' }}>
                                                {availableCoupons.map((c) => {
                                                    const isApplied = appliedCoupon?.code === c.code;
                                                    const isLimitReached = Boolean(c.isLimitReached);
                                                    const discount = calculateCouponDiscount(c, subtotal);
                                                    const minSpend = parseFloat(c.minOrderValue ?? c.min_order ?? 0);
                                                    const isEligible = !isLimitReached && subtotal >= minSpend;
                                                    const cType = (c.type || 'PERCENT').toUpperCase();

                                                    return (
                                                        <div
                                                            key={c.id}
                                                            onClick={() => {
                                                                if (isEligible && !isApplied) {
                                                                    handleSelectCoupon(c);
                                                                }
                                                            }}
                                                            className={`co-coupon-drawer-card ${isApplied ? 'applied' : isLimitReached || !isEligible ? 'ineligible' : ''}`}
                                                            style={{
                                                                cursor: isEligible && !isApplied ? 'pointer' : 'not-allowed',
                                                                padding: '10px 12px',
                                                                opacity: isLimitReached ? 0.6 : 1,
                                                            }}
                                                        >
                                                            <div className="flex-between flex-wrap gap-6 mb-4">
                                                                <div className="flex-align gap-6">
                                                                    <span className="co-coupon-code-tag">
                                                                        {c.code}
                                                                    </span>
                                                                    {c.code === bestCoupon?.code && isEligible && (
                                                                        <span
                                                                            style={{
                                                                                fontSize: '9.5px',
                                                                                fontWeight: 700,
                                                                                padding: '2px 7px',
                                                                                borderRadius: '999px',
                                                                                background: '#DCFCE7',
                                                                                color: '#166534',
                                                                                border: '1px solid #BBF7D0',
                                                                                lineHeight: 1.2,
                                                                            }}
                                                                        >
                                                                            ★ Best Value
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                {isApplied ? (
                                                                    <span
                                                                        style={{
                                                                            fontSize: '11px',
                                                                            fontWeight: 700,
                                                                            color: '#047857',
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            gap: '4px',
                                                                            lineHeight: 1.2,
                                                                        }}
                                                                    >
                                                                        <i className="ph-fill ph-check-circle" style={{ fontSize: '13px' }} /> Applied
                                                                    </span>
                                                                ) : isLimitReached ? (
                                                                    <span
                                                                        style={{
                                                                            fontSize: '10.5px',
                                                                            fontWeight: 600,
                                                                            color: '#64748B',
                                                                            background: '#F1F5F9',
                                                                            padding: '3px 8px',
                                                                            borderRadius: '5px',
                                                                            border: '1px solid #E2E8F0',
                                                                            lineHeight: 1.2,
                                                                        }}
                                                                    >
                                                                        Limit Reached
                                                                    </span>
                                                                ) : isEligible ? (
                                                                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                                                        {discount > 0 && (
                                                                            <span
                                                                                style={{
                                                                                    fontSize: '10px',
                                                                                    fontWeight: 700,
                                                                                    padding: '2px 7px',
                                                                                    borderRadius: '5px',
                                                                                    background: '#ECFDF5',
                                                                                    color: '#059669',
                                                                                    border: '1px solid #A7F3D0',
                                                                                    lineHeight: 1.2,
                                                                                }}
                                                                            >
                                                                                Saves ₹{discount.toFixed(0)}
                                                                            </span>
                                                                        )}
                                                                        <span
                                                                            style={{
                                                                                fontSize: '10.5px',
                                                                                fontWeight: 700,
                                                                                padding: '3px 9px',
                                                                                borderRadius: '5px',
                                                                                background: '#EFF6FF',
                                                                                color: '#2563EB',
                                                                                border: '1px solid #BFDBFE',
                                                                                letterSpacing: '0.2px',
                                                                                lineHeight: 1.2,
                                                                            }}
                                                                        >
                                                                            Apply
                                                                        </span>
                                                                    </div>
                                                                ) : (
                                                                    <span
                                                                        style={{
                                                                            fontSize: '10.5px',
                                                                            fontWeight: 600,
                                                                            color: '#64748B',
                                                                            background: '#F1F5F9',
                                                                            padding: '3px 8px',
                                                                            borderRadius: '5px',
                                                                            border: '1px solid #E2E8F0',
                                                                            lineHeight: 1.2,
                                                                        }}
                                                                    >
                                                                        Add ₹{(minSpend - subtotal).toFixed(0)} more
                                                                    </span>
                                                                )}
                                                            </div>

                                                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#0F172A', marginBottom: '3px' }}>
                                                                {cType === 'PERCENT'
                                                                    ? `${Math.round(parseFloat(c.value || 0))}% OFF${c.maxDiscount ? ` (Up to ₹${Math.round(parseFloat(c.maxDiscount))})` : ''}`
                                                                    : cType === 'FREE_SHIPPING'
                                                                    ? '100% Free Shipping on Delivery'
                                                                    : `Flat ₹${Math.round(parseFloat(c.value || 0))} OFF`}
                                                            </div>

                                                            {c.description && (
                                                                <p style={{ fontSize: '11px', color: '#64748B', lineHeight: '1.4', margin: '0 0 6px 0' }}>
                                                                    {c.description}
                                                                </p>
                                                            )}

                                                            <div style={{ fontSize: '10.5px', color: '#94A3B8', paddingTop: '6px', marginTop: '6px', borderTop: '1px solid #F1F5F9' }}>
                                                                <span>
                                                                    {minSpend > 0 ? `Min Order: ₹${minSpend.toFixed(0)}` : 'No Minimum Order'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* ── 🚀 Shiprocket Delivery Partner Card ── */}
                            <div className="p-12 p-sm-16 rounded-12 mb-16" style={{ background: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                                <div className="d-flex align-items-center justify-content-between flex-wrap gap-8 mb-8">
                                    <div className="d-flex align-items-center gap-10">
                                        <span className="p-2 rounded-8 text-white flex-center flex-shrink-0" style={{ width: 34, height: 34, background: '#10B981' }}>
                                            <i className="ph-fill ph-truck text-base" />
                                        </span>
                                        <div>
                                            <span className="text-xs text-gray-500 d-block fw-medium">Delivery Partner</span>
                                            <span className="text-sm text-neutral-900 fw-bold">{courierName}</span>
                                        </div>
                                    </div>
                                    <div className="text-end ms-auto">
                                        <span className="fw-bold text-base text-neutral-900 d-block">
                                            {!selectedAddress?.pincode ? (
                                                <span className="text-gray-500 text-xs fw-normal">Enter pincode</span>
                                            ) : isShippingLoading ? (
                                                <span className="text-xs text-gray-400 font-normal">Calculating...</span>
                                            ) : shippingCharge === 0 ? (
                                                <span className="text-success">FREE</span>
                                            ) : (
                                                `₹${shippingCharge.toFixed(2)}`
                                            )}
                                        </span>
                                        {selectedAddress?.pincode && !isShippingLoading && (
                                            <span className="text-xs text-gray-500">Est. {estimatedDeliveryDays} days</span>
                                        )}
                                    </div>
                                </div>

                                {isShippingLoading ? (
                                    <div className="pt-8 border-top border-gray-200 mt-8">
                                        <span className="text-xs text-main-600 flex-align gap-6">
                                            <i className="ph ph-spinner-gap ph-spin text-sm" /> Calculating live courier rates...
                                        </span>
                                    </div>
                                ) : (
                                    <div className="pt-8 border-top border-gray-200 mt-8 flex-between flex-wrap gap-6">
                                        <button
                                            type="button"
                                            onClick={handleOpenCourierModal}
                                            className="btn p-0 text-main-600 hover-text-main-800 text-xs fw-bold flex-align gap-6 transition"
                                            style={{ background: 'transparent', border: 'none' }}
                                        >
                                            <i className="ph ph-arrows-clockwise text-sm" />
                                            <span>Change Delivery Partner</span>
                                            {availableCouriers.length > 1 && (
                                                <span className="text-gray-400 font-normal">({availableCouriers.length} options)</span>
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Totals Breakdown */}
                            <div className="co-totals-row">
                                <span className="text-gray-600 text-xs">Subtotal</span>
                                <span className="fw-semibold text-xs text-neutral-900">₹{subtotal.toFixed(2)}</span>
                            </div>

                            {appliedCoupon && (
                                <div className="co-totals-row text-success text-xs">
                                    <span>Coupon Discount ({appliedCoupon.code})</span>
                                    <span className="fw-semibold">-₹{couponDiscount.toFixed(2)}</span>
                                </div>
                            )}

                            <div className="co-totals-row text-xs">
                                <span className="text-gray-600">Shipping ({courierName})</span>
                                <span className="fw-semibold text-neutral-900">
                                    {!selectedAddress?.pincode ? (
                                        <span className="text-gray-500 font-normal">Enter pincode</span>
                                    ) : isShippingLoading ? (
                                        <span className="text-gray-400 font-normal">Calculating...</span>
                                    ) : shippingCharge === 0 ? (
                                        <span className="text-success">FREE</span>
                                    ) : (
                                        `₹${shippingCharge.toFixed(2)}`
                                    )}
                                </span>
                            </div>

                            <div className="co-totals-row total mt-8 pt-8 border-top">
                                <span className="fw-bold">Grand Total</span>
                                <span className="text-main-600 fw-bold text-lg">₹{finalTotal.toFixed(2)}</span>
                            </div>
                            <div className="text-gray-500 text-xs mt-4" style={{ fontSize: '12px' }}>
                                (Inclusive of GST)
                            </div>

                            {/* Place Order CTA */}
                            <button
                                type="button"
                                className="btn btn-main mt-20 py-14 w-100 rounded-8 font-heading fw-bold"
                                disabled={!selectedAddressId || isPlacingOrder || isShippingLoading}
                                onClick={handlePlaceOrder}
                            >
                                {isPlacingOrder ? 'Processing Order...' : `Pay ₹${finalTotal.toFixed(2)} via Razorpay`}
                            </button>

                            {!selectedAddressId && (
                                <p className="text-danger-600 text-xs text-center mt-8 mb-0">Please select or add a delivery address to place your order.</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── 🚚 Delivery Partner Selection Modal ── */}
            {showCourierModal && (
                <div
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{
                        background: 'rgba(15, 23, 42, 0.7)',
                        backdropFilter: 'blur(5px)',
                        zIndex: 99999,
                        padding: '12px',
                    }}
                    onClick={() => setShowCourierModal(false)}
                >
                    <div
                        className="bg-white rounded-16 rounded-sm-20 shadow-2xl overflow-hidden w-100"
                        style={{
                            maxWidth: '560px',
                            maxHeight: '90vh',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="p-16 p-sm-20 px-sm-24 border-bottom border-gray-100 d-flex align-items-center justify-content-between bg-white" style={{ flexShrink: 0 }}>
                            <div className="d-flex align-items-center gap-10">
                                <div
                                    className="rounded-12 flex-center text-white flex-shrink-0"
                                    style={{ width: 36, height: 36, background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}
                                >
                                    <i className="ph-fill ph-truck text-lg" />
                                </div>
                                <div>
                                    <h5 className="text-base text-sm-lg mb-0 fw-bold text-neutral-900 font-heading">Select Delivery Partner</h5>
                                    <p className="text-xs text-gray-500 mb-0">
                                        Pincode <strong className="text-neutral-800">{selectedAddress?.pincode}</strong>
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowCourierModal(false)}
                                className="w-32 h-32 rounded-circle border border-gray-200 flex-center bg-gray-100 text-gray-700 hover-bg-gray-200 hover-text-neutral-900 transition flex-shrink-0"
                                aria-label="Close"
                                title="Close modal"
                            >
                                <i className="ph-bold ph-x text-sm text-neutral-900" />
                            </button>
                        </div>

                        {/* Modal Body: Courier Options List */}
                        <div className="p-16 p-sm-20 px-sm-24 overflow-y-auto" style={{ flex: '1 1 auto', minHeight: 0, maxHeight: 'calc(88vh - 140px)', background: '#FAFAFA' }}>
                            {availableCouriers.length === 0 ? (
                                <div className="text-center py-40 bg-white rounded-12 border border-dashed border-gray-200">
                                    <i className="ph ph-truck text-3xl text-gray-400 mb-8 d-block" />
                                    <p className="text-sm text-gray-600 mb-0">No courier services serviceable for this pincode.</p>
                                </div>
                            ) : (
                                <div className="d-flex flex-column gap-10">
                                    {availableCouriers.map((courier, idx) => {
                                        const rateVal = parseFloat(courier.charge !== undefined ? courier.charge : (courier.rate || 0));
                                        const isSelected = tempSelectedCourier && String(tempSelectedCourier.id) === String(courier.id);

                                        return (
                                            <div
                                                key={courier.id || idx}
                                                onClick={() => setTempSelectedCourier(courier)}
                                                className={`p-12 p-sm-16 rounded-12 border cursor-pointer transition position-relative ${
                                                    isSelected ? 'shadow-sm' : 'hover-border-gray-300'
                                                }`}
                                                style={{
                                                    background: isSelected ? '#F0FDF4' : '#FFFFFF',
                                                    borderColor: isSelected ? '#10B981' : '#E5E7EB',
                                                    borderWidth: isSelected ? '2px' : '1.5px',
                                                }}
                                            >
                                                <div className="d-flex align-items-center justify-content-between gap-10 flex-wrap sm:flex-nowrap">
                                                    {/* Left: Custom Radio + Details */}
                                                    <div className="d-flex align-items-center gap-10 flex-grow-1 min-w-0">
                                                        <div
                                                            className="flex-center rounded-circle flex-shrink-0 transition"
                                                            style={{
                                                                width: 20,
                                                                height: 20,
                                                                border: isSelected ? '6px solid #10B981' : '2px solid #D1D5DB',
                                                                background: '#FFFFFF',
                                                            }}
                                                        />

                                                        <div className="min-w-0">
                                                            <div className="text-xs text-sm-sm fw-bold text-neutral-900 mb-2 text-truncate">
                                                                {courier.name}
                                                            </div>
                                                            <div className="d-flex align-items-center gap-8 flex-wrap">
                                                                <span className="flex-align gap-4 text-xs text-gray-600">
                                                                    <i className="ph ph-clock text-xs text-main-600" />
                                                                    Est: <strong className="text-neutral-800">{courier.days || 4} days</strong>
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Right: Price */}
                                                    <div className="text-end flex-shrink-0 ms-auto ps-8">
                                                        <span className="text-sm text-sm-base fw-bold text-neutral-900 d-block">
                                                            ₹{rateVal.toFixed(2)}
                                                        </span>
                                                        <span className="text-[10px] text-gray-400">Shipping</span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div
                            className="p-16 px-sm-24 border-top border-gray-200 d-flex align-items-center gap-12 bg-white"
                            style={{ flexShrink: 0, zIndex: 10 }}
                        >
                            <button
                                type="button"
                                onClick={() => setShowCourierModal(false)}
                                className="d-flex align-items-center justify-content-center gap-6 rounded-10 text-sm fw-bold transition"
                                style={{
                                    height: '46px',
                                    padding: '0 20px',
                                    minWidth: '110px',
                                    background: '#F1F5F9',
                                    border: '1.5px solid #CBD5E1',
                                    color: '#334155',
                                    cursor: 'pointer',
                                    flexShrink: 0,
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#E2E8F0';
                                    e.currentTarget.style.borderColor = '#94A3B8';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = '#F1F5F9';
                                    e.currentTarget.style.borderColor = '#CBD5E1';
                                }}
                            >
                                <i className="ph-bold ph-x text-sm" />
                                <span>Cancel</span>
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmCourier}
                                className="d-flex align-items-center justify-content-center gap-8 rounded-10 text-sm fw-bold transition shadow-sm"
                                style={{
                                    height: '46px',
                                    padding: '0 20px',
                                    flex: '1 1 auto',
                                    minWidth: 0,
                                    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                                    border: 'none',
                                    color: '#FFFFFF',
                                    cursor: 'pointer',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(135deg, #059669 0%, #047857 100%)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(135deg, #10B981 0%, #059669 100%)';
                                }}
                            >
                                <i className="ph-bold ph-check text-base" />
                                <span className="text-truncate">
                                    Apply {tempSelectedCourier?.name ? tempSelectedCourier.name : 'Partner'} (₹{parseFloat(tempSelectedCourier?.charge !== undefined ? tempSelectedCourier?.charge : (tempSelectedCourier?.rate || 0)).toFixed(2)})
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Address Form Modal ── */}
            {showAddressForm && (
                <div
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{
                        background: 'rgba(15, 23, 42, 0.65)',
                        backdropFilter: 'blur(4px)',
                        zIndex: 9999,
                        padding: '12px',
                    }}
                    onClick={closeForm}
                >
                    <div
                        className="bg-white rounded-16 rounded-sm-20 shadow-2xl overflow-hidden w-100"
                        style={{ maxWidth: '560px', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-16 p-sm-20 border-bottom border-gray-100 flex-between bg-white">
                            <div className="d-flex align-items-center gap-10">
                                <span className="p-2 rounded-8 text-white flex-center flex-shrink-0" style={{ width: 34, height: 34, background: '#10B981' }}>
                                    <i className="ph-fill ph-map-pin text-base" />
                                </span>
                                <div>
                                    <h6 className="text-base mb-0 fw-bold text-neutral-900 font-heading">{editingAddressId ? 'Edit Address' : 'Add New Address'}</h6>
                                    <span className="text-xs text-gray-500">Provide accurate delivery address details</span>
                                </div>
                            </div>
                            <button type="button" onClick={closeForm} className="btn-close text-xs" aria-label="Close" />
                        </div>

                        <form onSubmit={saveAddress} className="p-16 p-sm-20 overflow-y-auto space-y-12" style={{ maxHeight: 'calc(90vh - 130px)' }}>
                            <div className="row g-2 g-sm-3">
                                <div className="col-12 col-sm-6">
                                    <label className="form-label text-xs fw-semibold text-gray-700 mb-4">Full Name *</label>
                                    <input
                                        type="text"
                                        required
                                        className="form-control text-sm py-8 px-12 rounded-8"
                                        placeholder="Full Name"
                                        value={addressForm.fullName}
                                        onChange={(e) => setAddressForm({ ...addressForm, fullName: e.target.value })}
                                    />
                                </div>
                                <div className="col-12 col-sm-6">
                                    <label className="form-label text-xs fw-semibold text-gray-700 mb-4">Mobile Number *</label>
                                    <input
                                        type="tel"
                                        required
                                        className="form-control text-sm py-8 px-12 rounded-8"
                                        placeholder="10-digit mobile number"
                                        value={addressForm.mobile}
                                        onChange={(e) => setAddressForm({ ...addressForm, mobile: e.target.value })}
                                    />
                                </div>
                                <div className="col-12">
                                    <label className="form-label text-xs fw-semibold text-gray-700 mb-4">Street Address *</label>
                                    <input
                                        type="text"
                                        required
                                        className="form-control text-sm py-8 px-12 rounded-8"
                                        placeholder="House / Flat No., Building Name, Street"
                                        value={addressForm.street}
                                        onChange={(e) => setAddressForm({ ...addressForm, street: e.target.value })}
                                    />
                                </div>
                                <div className="col-12">
                                    <label className="form-label text-xs fw-semibold text-gray-700 mb-4">Landmark / Apartment</label>
                                    <input
                                        type="text"
                                        className="form-control text-sm py-8 px-12 rounded-8"
                                        placeholder="Near milestone, area"
                                        value={addressForm.landmark}
                                        onChange={(e) => setAddressForm({ ...addressForm, landmark: e.target.value })}
                                    />
                                </div>
                                <div className="col-6 col-sm-4">
                                    <label className="form-label text-xs fw-semibold text-gray-700 mb-4">City *</label>
                                    <input
                                        type="text"
                                        required
                                        className="form-control text-sm py-8 px-12 rounded-8"
                                        placeholder="City"
                                        value={addressForm.city}
                                        onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                                    />
                                </div>
                                <div className="col-6 col-sm-4">
                                    <label className="form-label text-xs fw-semibold text-gray-700 mb-4">State *</label>
                                    <input
                                        type="text"
                                        required
                                        className="form-control text-sm py-8 px-12 rounded-8"
                                        placeholder="State"
                                        value={addressForm.state}
                                        onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })}
                                    />
                                </div>
                                <div className="col-12 col-sm-4">
                                    <label className="form-label text-xs fw-semibold text-gray-700 mb-4">Pincode *</label>
                                    <input
                                        type="text"
                                        required
                                        maxLength={6}
                                        className="form-control text-sm py-8 px-12 rounded-8"
                                        placeholder="6-digit pincode"
                                        value={addressForm.pincode}
                                        onChange={(e) => setAddressForm({ ...addressForm, pincode: e.target.value })}
                                    />
                                </div>
                                <div className="col-12">
                                    <label className="form-label text-xs fw-semibold text-gray-700 mb-4">Address Type</label>
                                    <div className="d-flex gap-16 flex-wrap">
                                        {['home', 'work', 'other'].map((t) => (
                                            <label key={t} className="d-flex align-items-center gap-6 text-xs text-capitalize cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="addressType"
                                                    value={t}
                                                    checked={addressForm.type === t}
                                                    onChange={() => setAddressForm({ ...addressForm, type: t })}
                                                    className="form-check-input mt-0"
                                                />
                                                {t}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div className="col-12">
                                    <label className="d-flex align-items-center gap-8 text-xs cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={addressForm.isDefault}
                                            onChange={(e) => setAddressForm({ ...addressForm, isDefault: e.target.checked })}
                                            className="form-check-input mt-0"
                                        />
                                        Set as default delivery address
                                    </label>
                                </div>
                            </div>

                            <div className="d-flex justify-content-end gap-10 mt-16 pt-12 border-top border-gray-100">
                                <button
                                    type="button"
                                    onClick={closeForm}
                                    className="rounded-8 px-18 py-8 text-xs fw-bold transition"
                                    style={{
                                        background: '#F1F5F9',
                                        border: '1.5px solid #94A3B8',
                                        color: '#0F172A',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <span style={{ color: '#0F172A', fontWeight: 700 }}>Cancel</span>
                                </button>
                                <button type="submit" className="btn btn-main text-xs px-20 py-8 rounded-8 fw-bold">
                                    {editingAddressId ? 'Update Address' : 'Save Address'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </section>
    );
};

export default Checkout;
